import { z } from 'zod';
/**
 * Tóm tắt một MR bằng AI — APM-GIT-m, docs/plan-git-integration.md §8.5, §8.6.
 *
 * Tệp riêng (không nối vào `git.ts`) vì các task `APM-GIT-*` chạy song song cùng
 * thêm kiểu.
 *
 * Skill `summarize_change_request` trả **ba câu**: đổi gì, chạm phần nào, liên
 * quan việc nào. Lời tóm tắt nằm ở `git_cr_summary`, khoá theo `(kho, số MR,
 * head_sha)`: MR chưa có commit mới thì không tốn AI lần hai. Output AI ở
 * `ai_suggested` tới khi người duyệt (luật #6).
 */
export const SUMMARIZE_CHANGE_REQUEST_SKILL = 'summarize_change_request';
/** Trần một câu của lời tóm tắt — ba câu ngắn, không phải một bài. */
export const GIT_CR_SUMMARY_SENTENCE_MAX = 400;
/** Trần chữ người sửa / viết tay. */
export const GIT_CR_SUMMARY_BODY_MAX = 2000;
/**
 * Lời tóm tắt dựa trên gì. `diff` = có đọc **mã** thay đổi (kho đã bật
 * `aiReadDiff` và tool trả về ít nhất một tệp có nội dung); `description` = chỉ
 * tiêu đề, mô tả, lời commit và đường dẫn tệp.
 *
 * ⚠️ Model **không** tự phong giá trị này: `finalize()` của skill đặt lại từ kết
 * quả tool của chính lượt chạy. Giao diện in nhãn theo nó — một nhãn "có đọc mã"
 * sai là nói dối người duyệt.
 */
export const GitCrSummaryBasis = z.enum(['diff', 'description']);
const Sentence = z.string().trim().min(1).max(GIT_CR_SUMMARY_SENTENCE_MAX);
/** Output của skill `summarize_change_request`. */
export const GitCrSummaryOutput = z.object({
    /** Câu 1 — MR đổi gì, nói bằng lời của người không đọc code. */
    what: Sentence,
    /** Câu 2 — chạm phần nào của hệ thống (module, màn hình, API…). */
    where: Sentence,
    /** Câu 3 — liên quan việc nào (mã task / issue), hoặc nói rõ là không thấy việc nào. */
    related: Sentence,
    basedOn: GitCrSummaryBasis.default('description'),
});
/** `git_cr_summary.status` — cùng enum `GitTextStatus` của `git_release_note`. */
export const GitCrSummaryStatus = z.enum(['ai_suggested', 'approved', 'manual']);
export const GitCrSummaryView = z.object({
    id: z.uuid(),
    repositoryId: z.uuid(),
    number: z.number().int().positive(),
    /** Đầu nhánh của MR lúc tóm tắt. */
    headSha: z.string(),
    /** Ba câu, mỗi câu một dòng (bản viết tay: chữ tự do). */
    body: z.string(),
    status: GitCrSummaryStatus,
    /** `true` = có đọc mã (nhãn "có đọc mã"); `false` = "dựa trên lời mô tả". */
    readDiff: z.boolean(),
    approvedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * `GET …/repositories/:repositoryId/change-requests/:number/summary` (`git:read`).
 *
 * `summary` = lời tóm tắt của **đầu nhánh hiện tại**; `null` = chưa có — "chưa
 * có" là một giá trị (bẫy "endpoint trả `null` trần"). `outdated` = bản mới nhất
 * của một đầu nhánh **cũ** (MR có commit mới từ lúc tóm tắt), chỉ có khi
 * `summary` là `null`.
 *
 * Nhận `asOf` (luật #7): chỉ bản tạo **trước** mốc; bản duyệt sau mốc hiện là
 * `ai_suggested`. Chữ của bản đã sửa tay là chữ hiện tại (sửa không giữ lịch sử
 * chữ trong bảng — lịch sử ở event `git_cr_summary.edited`).
 */
export const GitCrSummaryState = z.object({
    /** Đầu nhánh hiện tại của MR (bản sao); `null` khi máy chủ git không cho biết. */
    headSha: z.string().nullable(),
    asOf: z.iso.datetime().nullable(),
    summary: GitCrSummaryView.nullable(),
    outdated: GitCrSummaryView.nullable(),
});
/**
 * `POST …/summary` (`git:read`) — chạy skill. Đầu nhánh đã có bản **đã duyệt /
 * viết tay** ⇒ trả lại bản ấy, không gọi AI (kể cả `refresh`). `refresh` chỉ
 * thay một bản `ai_suggested`.
 */
export const RunGitCrSummaryInput = z.object({
    /** Mã dấu vết để kể lại từng bước ở bảng bên phải (APM-1302). */
    runId: z.uuid().optional(),
    refresh: z.boolean().optional(),
});
/** `PUT …/summary` (`git:act`) — sửa bản AI hoặc viết tay ⇒ `manual`, cho đầu nhánh hiện tại. */
export const WriteGitCrSummaryInput = z.object({
    body: z.string().trim().min(1).max(GIT_CR_SUMMARY_BODY_MAX),
});
/** Ba câu của skill → chữ lưu ở `git_cr_summary.body`, mỗi câu một dòng. */
export function gitCrSummaryBody(output) {
    return [output.what, output.where, output.related].map((line) => line.trim()).join('\n');
}
//# sourceMappingURL=git-cr-summary.js.map